/**
 * ActionLog.jsx — Activity log display
 *
 * Features:
 * - Chronological list (newest first)
 * - Each entry shows: timestamp, user, action, job reference (if any), details summary
 * - Category filter buttons (or search)
 * - Compact card-style entries
 * - Color coding by action type
 */

import { useState, useMemo } from 'react';
import { fmtD, cn } from '../../lib/utils';
import { useActionLog } from '../../hooks/useActionLog';

const ACTION_COLORS = {
  create: 'bg-green/10 border-green/30',
  update: 'bg-blue/10 border-blue/30',
  delete: 'bg-red-accent/10 border-red-accent/30',
  restore: 'bg-gold/10 border-gold/30',
  view: 'bg-teal/10 border-teal/30',
  export: 'bg-purple/10 border-purple/30',
  import: 'bg-purple/10 border-purple/30',
  login: 'bg-teal/10 border-teal/30',
  logout: 'bg-teal/10 border-teal/30',
};

const ACTION_TEXT_COLORS = {
  create: 'text-green',
  update: 'text-blue',
  delete: 'text-red-accent',
  restore: 'text-gold',
  view: 'text-teal',
  export: 'text-purple',
  import: 'text-purple',
  login: 'text-teal',
  logout: 'text-teal',
};

function getActionCategory(action) {
  if (!action) return 'other';
  const lower = action.toLowerCase();
  // 'Restore Job' / 'Restore Invoice' from Settings ▸ Trash.
  if (lower.includes('restore')) return 'restore';
  if (lower.includes('create') || lower.includes('add')) return 'create';
  if (lower.includes('update') || lower.includes('edit') || lower.includes('change')) return 'update';
  // 'Purge Job' / 'Purge Invoice': the daily Trash purge, gone for good.
  if (lower.includes('delete') || lower.includes('remove') || lower.includes('purge')) return 'delete';
  if (lower.includes('view') || lower.includes('read')) return 'view';
  if (lower.includes('export') || lower.includes('download')) return 'export';
  if (lower.includes('import') || lower.includes('upload')) return 'import';
  if (lower.includes('login') || lower.includes('signin')) return 'login';
  if (lower.includes('logout') || lower.includes('signout')) return 'logout';
  return 'other';
}

const PAGE_SIZE = 50;

export default function ActionLog({ search = '' }) {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { data, isLoading, error } = useActionLog(limit);
  const entries = data?.entries || [];
  const total = data?.total || 0;
  const [categoryFilter, setCategoryFilter] = useState('All');

  // ── Computed list ────────────────────────────────────────────────────────
  const filteredEntries = useMemo(() => {
    let result = entries;

    // Filter by category
    if (categoryFilter !== 'All') {
      result = result.filter((e) => getActionCategory(e.action) === categoryFilter.toLowerCase());
    }

    // Filter by search (action, user, description, jobTitle)
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (e) =>
          (e.action || '').toLowerCase().includes(q) ||
          (e.user || '').toLowerCase().includes(q) ||
          (e.description || '').toLowerCase().includes(q) ||
          (e.jobTitle || '').toLowerCase().includes(q)
      );
    }

    // Already sorted newest first from the hook
    return result;
  }, [entries, search, categoryFilter]);

  // Group by date for date headers (A2)
  const groupedByDate = useMemo(() => {
    const groups = [];
    let currentDate = null;

    filteredEntries.forEach((entry) => {
      const dateStr = entry.createdAt
        ? new Date(entry.createdAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
        : 'Unknown Date';

      if (dateStr !== currentDate) {
        currentDate = dateStr;
        groups.push({ date: dateStr, entries: [] });
      }
      groups[groups.length - 1].entries.push(entry);
    });

    return groups;
  }, [filteredEntries]);

  if (error) {
    return (
      <div className="card p-4 text-red-accent text-sm">
        Failed to load activity log: {error.message}
      </div>
    );
  }

  const categories = ['All', 'Create', 'Update', 'Delete', 'Restore', 'View', 'Export', 'Import', 'Login'];

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={cn(
              'px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors',
              categoryFilter === cat
                ? 'bg-accent text-bg'
                : 'bg-surface border border-border text-text-dim hover:border-accent'
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Activity List */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {isLoading ? (
          <div className="text-text-dim text-sm p-4">Loading activity...</div>
        ) : groupedByDate.length === 0 ? (
          <div className="text-text-dim text-sm p-4">No activity found</div>
        ) : (
          groupedByDate.map((group) => (
            <div key={group.date}>
              {/* Date header (A2) */}
              <div className="sticky top-0 bg-bg py-1 mb-1 z-10">
                <span className="text-2xs font-semibold text-text-dim uppercase tracking-wide">{group.date}</span>
              </div>
              <div className="space-y-2 mb-4">
                {group.entries.map((entry) => {
                  const category = getActionCategory(entry.action);
                  const bgColor = ACTION_COLORS[category] || 'bg-surface border-border';
                  const textColor = ACTION_TEXT_COLORS[category] || 'text-text-primary';

                  return (
                    <div
                      key={entry.id}
                      className={cn(
                        'border rounded-lg p-3 transition-colors hover:bg-surface/50',
                        bgColor
                      )}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={cn('font-medium text-sm', textColor)}>
                              {entry.action || 'Unknown Action'}
                            </span>
                            <span className="text-text-dim text-xs">by {entry.user || 'Unknown'}</span>
                          </div>
                          <div className="text-text-dim text-xs mt-0.5">
                            {entry.createdAt
                              ? new Date(entry.createdAt).toLocaleTimeString()
                              : '—'}
                          </div>
                        </div>
                      </div>
                      {entry.jobId && (
                        <div className="text-text-dim text-xs mb-1">
                          Job: <span className="text-text-primary">{entry.jobTitle || entry.jobId}</span>
                        </div>
                      )}
                      {entry.description && (
                        <div className="text-text-dim text-xs">{entry.description}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {/* Load More / Count */}
        {!isLoading && entries.length > 0 && (
          <div className="flex items-center justify-between pt-2 pb-1 text-xs text-text-dim">
            <span>Showing {filteredEntries.length} of {total} entries</span>
            {entries.length < total && (
              <button
                onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
                className="px-3 py-1 bg-surface border border-border rounded text-xs text-accent hover:border-accent transition-colors"
              >
                Load More
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
